import { ProjectState, StartProjectResponse, ProjectHistoryItem } from './types';

const API_BASE = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8002';
const API_VERSION = 'v1';
const API_URL = `${API_BASE}/api/${API_VERSION}`;

export class ProjectService {
    static get token(): string | null {
        if (typeof window === 'undefined') return null;
        return localStorage.getItem('vet_token');
    }

    static set token(value: string | null) {
        if (typeof window === 'undefined') return;
        if (value) localStorage.setItem('vet_token', value);
        else localStorage.removeItem('vet_token');
    }

    static async login(username: string, password: string): Promise<boolean> {
        const formData = new URLSearchParams();
        formData.append('username', username);
        formData.append('password', password);

        const res = await fetch(`${API_URL}/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: formData
        });

        if (res.ok) {
            const data = await res.json();
            this.token = data.access_token;
            return true;
        }
        return false;
    }

    static async register(data: Record<string, unknown>): Promise<boolean> {
        const res = await fetch(`${API_URL}/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        });

        return res.ok;
    }

    static async verifyEmail(token: string): Promise<{ success: boolean; message?: string }> {
        const res = await fetch(`${API_URL}/verify-email?token=${token}`, {
            method: 'GET',
            headers: this.getHeaders()
        });

        if (res.ok) {
            return { success: true };
        }
        const data = await res.json().catch(() => ({ detail: 'Verification failed' }));
        return { success: false, message: data.detail || 'Verification failed' };
    }

    static async getProfile(): Promise<Record<string, unknown>> {
        const res = await fetch(`${API_URL}/me`, {
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error(`Failed to fetch profile: ${res.status}`);
        return res.json();
    }

    static logout() {
        this.token = null;
    }

    static async changePassword(oldPassword: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
        const res = await fetch(`${API_URL}/auth/change_password`, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify({ old_password: oldPassword, new_password: newPassword })
        });

        if (res.ok) {
            return { success: true };
        }
        const data = await res.json().catch(() => ({ detail: 'Password change failed' }));
        return { success: false, error: data.detail || 'Password change failed' };
    }

    static async updateProfile(data: { email?: string; full_name?: string }): Promise<{ success: boolean; error?: string }> {
        const res = await fetch(`${API_URL}/me`, {
            method: 'PUT',
            headers: this.getHeaders(),
            body: JSON.stringify(data)
        });

        if (res.ok) {
            return { success: true };
        }
        const errData = await res.json().catch(() => ({ detail: 'Update failed' }));
        return { success: false, error: errData.detail || 'Update failed' };
    }

    static getHeaders(): HeadersInit {
        const headers: HeadersInit = { 'Content-Type': 'application/json' };
        if (this.token) {
            headers['Authorization'] = `Bearer ${this.token}`;
        }
        return headers;
    }

    static async downloadFile(url: string, defaultFilename: string): Promise<void> {
        const headers: HeadersInit = {};
        if (this.token) {
            headers['Authorization'] = `Bearer ${this.token}`;
        }

        const res = await fetch(url, { headers });

        if (!res.ok) throw new Error(`Download failed: ${res.status}`);

        let filename = defaultFilename;
        const disposition = res.headers.get('content-disposition');
        if (disposition && disposition.indexOf('attachment') !== -1) {
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(disposition);
            if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, '');
            }
        }

        const blob = await res.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
    }

    static async downloadProjectExport(projectId: string): Promise<void> {
        await this.downloadFile(`${API_URL}/project/${projectId}/export`, `vet_project_${projectId}.zip`);
    }
    static async start(useCase: string, agentModels: Record<string, any> = {}, attachments: { filename: string, content: string }[] = [], attachment_ids: string[] = []): Promise<StartProjectResponse> {
        const { auto_approve, ...models } = agentModels;
        const res = await fetch(`${API_URL}/start_project`, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify({
                use_case: useCase,
                autonomous_mode: !!auto_approve,
                agent_models: models,
                attachments: attachments,
                attachment_ids: attachment_ids
            })
        });

        if (!res.ok) {
            throw new Error(`Failed to start project: ${res.status}`);
        }

        return res.json();
    }

    static async getModels(): Promise<{ models: string[] }> {
        const res = await fetch(`${API_URL}/models`);
        if (!res.ok) {
            throw new Error(`Failed to fetch models: ${res.status}`);
        }
        return res.json();
    }

    static async getState(projectId: string): Promise<ProjectState> {
        const res = await fetch(`${API_URL}/project/${projectId}/state`, {
            headers: this.getHeaders()
        });

        if (!res.ok) {
            throw new Error(`Failed to get project state: ${res.status}`);
        }

        return res.json();
    }

    static async getSmartBlueprint(projectId: string): Promise<any> {
        const res = await fetch(`${API_URL}/project/${projectId}/blueprint-summary`, {
            headers: this.getHeaders()
        });

        if (!res.ok) {
            throw new Error(`Failed to get smart blueprint: ${res.status}`);
        }

        return res.json();
    }

    static async stop(projectId: string): Promise<{ status: string }> {
        const res = await fetch(`${API_URL}/stop_project`, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify({ project_id: projectId })
        });

        if (!res.ok) {
            throw new Error(`Failed to stop project: ${res.status}`);
        }

        return res.json();
    }

    static async addPrompt(projectId: string, prompt: string): Promise<{ status: string; message: string }> {
        const res = await fetch(`${API_URL}/project/${projectId}/prompt`, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify({ prompt })
        });

        if (!res.ok) {
            throw new Error(`Failed to add prompt: ${res.status}`);
        }

        return res.json();
    }

    static async approve(projectId: string, approved: boolean, feedback: string = ""): Promise<{ status: string; message: string }> {
        const res = await fetch(`${API_URL}/project/${projectId}/approve`, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify({ approved, feedback })
        });

        if (!res.ok) {
            throw new Error(`Failed to approve/reject: ${res.status}`);
        }

        return res.json();
    }

    static async resume(projectId: string): Promise<{ status: string; message: string }> {
        const res = await fetch(`${API_URL}/project/${projectId}/resume`, {
            method: 'POST',
            headers: this.getHeaders()
        });

        if (!res.ok) {
            throw new Error(`Failed to resume project: ${res.status}`);
        }

        return res.json();
    }

    static async list(): Promise<{ projects: ProjectHistoryItem[] }> {
        const res = await fetch(`${API_URL}/projects`, {
            headers: this.getHeaders()
        });

        if (!res.ok) {
            throw new Error(`Failed to list projects: ${res.status}`);
        }

        return res.json();
    }

    static async download(projectId: string): Promise<void> {
        const res = await fetch(`${API_URL}/project/${projectId}/download`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${this.token}`
            }
        });

        if (!res.ok) {
            throw new Error(`Failed to download project: ${res.status}`);
        }

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `vet_workspace_${projectId.substring(0, 8)}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }

    static async rollback(projectId: string): Promise<{ status: string }> {
        const res = await fetch(`${API_URL}/project/${projectId}/rollback`, {
            method: 'POST',
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error("Failed to rollback project");
        return res.json();
    }

    static async runTerminalCommand(projectId: string, command: string, ticketId?: string): Promise<{ stdout: string; stderr: string; exit_code: number }> {
        const res = await fetch(`${API_URL}/project/${projectId}/terminal${ticketId ? `?ticket_id=${ticketId}` : ''}`, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify({ command })
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || "Failed to run command");
        }
        return res.json();
    }

    static async deleteProject(projectId: string): Promise<{ status: string }> {
        const res = await fetch(`${API_URL}/project/${projectId}`, {
            method: 'DELETE',
            headers: this.getHeaders()
        });

        if (!res.ok) {
            throw new Error(`Failed to delete project: ${res.status}`);
        }

        return res.json();
    }

    static getSSEUrl(projectId: string): string {
        return `${API_URL}/stream/${projectId}`;
    }

    static async getPreferences(): Promise<{ default_model: string; notifications: string; theme: string }> {
        const res = await fetch(`${API_URL}/user/preferences`, {
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error('Failed to fetch preferences');
        return res.json();
    }

    static async updatePreferences(data: { default_model: string; notifications: string; theme: string }): Promise<void> {
        const res = await fetch(`${API_URL}/user/preferences`, {
            method: 'PUT',
            headers: this.getHeaders(),
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error('Failed to update preferences');
        return res.json();
    }

    static async getApiKeys(): Promise<{ id: string; name: string; key: string; created_at: string }[]> {
        const res = await fetch(`${API_URL}/user/api-keys`, {
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error('Failed to fetch API keys');
        return res.json();
    }

    static async createApiKey(name: string): Promise<{ id: string; name: string; key: string; created_at: string }> {
        const res = await fetch(`${API_URL}/user/api-keys`, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify({ name })
        });
        if (!res.ok) throw new Error('Failed to create API key');
        return res.json();
    }

    static async deleteApiKey(keyId: string): Promise<void> {
        const res = await fetch(`${API_URL}/user/api-keys/${keyId}`, {
            method: 'DELETE',
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error('Failed to delete API key');
        return res.json();
    }

    static async getOrganizationSettings(orgId: string): Promise<{ deepinfra_api_key: string | null; openai_api_key: string | null; anthropic_api_key: string | null }> {
        const res = await fetch(`${API_URL}/orgs/${orgId}/settings`, {
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error('Failed to fetch organization settings');
        return res.json();
    }

    static async updateOrganizationSettings(orgId: string, data: { deepinfra_api_key?: string | null; openai_api_key?: string | null; anthropic_api_key?: string | null }): Promise<{ deepinfra_api_key: string | null; openai_api_key: string | null; anthropic_api_key: string | null }> {
        const res = await fetch(`${API_URL}/orgs/${orgId}/settings`, {
            method: 'PUT',
            headers: this.getHeaders(),
            body: JSON.stringify(data)
        });
        if (!res.ok) throw new Error('Failed to update organization settings');
        return res.json();
    }

    static async getFile(projectId: string, path: string, ticketId?: string): Promise<{ content: string }> {
        const url = new URL(`${API_URL}/project/${projectId}/file`);
        url.searchParams.append('path', path);
        if (ticketId) url.searchParams.append('ticket_id', ticketId);
        
        const res = await fetch(url.toString(), {
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error(`Failed to fetch file: ${res.status}`);
        return res.json();
    }

    static async updateFile(projectId: string, path: string, content: string, ticketId?: string): Promise<{ status: string }> {
        const url = new URL(`${API_URL}/project/${projectId}/file`);
        if (ticketId) url.searchParams.append('ticket_id', ticketId);
        
        const res = await fetch(url.toString(), {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify({ path, content })
        });
        if (!res.ok) throw new Error(`Failed to update file: ${res.status}`);
        return res.json();
    }

    static async getFileStructure(projectId: string, ticketId?: string): Promise<Record<string, string>> {
        const url = new URL(`${API_URL}/project/${projectId}/structure`);
        if (ticketId) url.searchParams.append('ticket_id', ticketId);
        
        const res = await fetch(url.toString(), {
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error(`Failed to fetch file structure: ${res.status}`);
        return res.json();
    }

    static async startPreview(projectId: string): Promise<{ url: string; port: number }> {
        const res = await fetch(`${API_URL}/project/${projectId}/preview/start`, {
            method: 'POST',
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error(`Failed to start preview: ${res.status}`);
        return res.json();
    }

    static async stopPreview(projectId: string): Promise<void> {
        const res = await fetch(`${API_URL}/project/${projectId}/preview/stop`, {
            method: 'POST',
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error(`Failed to stop preview: ${res.status}`);
    }

    static async getPreviewLogs(projectId: string, tail: number = 100): Promise<{ logs: string }> {
        const res = await fetch(`${API_URL}/project/${projectId}/preview/logs?tail=${tail}`, {
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error(`Failed to fetch preview logs: ${res.status}`);
        return res.json();
    }

    static async getMembers(orgId: string): Promise<{ user_id: string; username: string; role: string; joined_at: string }[]> {
        const res = await fetch(`${API_URL}/org/${orgId}/members`, {
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error(`Failed to fetch members: ${res.status}`);
        return res.json();
    }

    static async inviteMember(orgId: string, email: string, role: string): Promise<{ status: string; message: string }> {
        const res = await fetch(`${API_URL}/org/${orgId}/invite`, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify({ email, role })
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || `Failed to invite member`);
        }
        return res.json();
    }

    static async removeMember(orgId: string, userId: string): Promise<{ status: string }> {
        const res = await fetch(`${API_URL}/org/${orgId}/members/${userId}`, {
            method: 'DELETE',
            headers: this.getHeaders()
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || `Failed to remove member`);
        }
        return res.json();
    }

    static async updateMemberRole(orgId: string, userId: string, role: string): Promise<{ status: string }> {
        const res = await fetch(`${API_URL}/org/${orgId}/members/${userId}/role`, {
            method: 'PATCH',
            headers: this.getHeaders(),
            body: JSON.stringify({ role })
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || `Failed to update member role`);
        }
        return res.json();
    }

    static async getPendingInvites(orgId: string): Promise<{ id: string; email: string; role: string; invited_by: string; created_at: string }[]> {
        const res = await fetch(`${API_URL}/org/${orgId}/invites`, {
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error(`Failed to fetch pending invites: ${res.status}`);
        return res.json();
    }

    static async cancelInvite(orgId: string, inviteId: string): Promise<{ status: string }> {
        const res = await fetch(`${API_URL}/org/${orgId}/invites/${inviteId}`, {
            method: 'DELETE',
            headers: this.getHeaders()
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || `Failed to cancel invite`);
        }
        return res.json();
    }
    static async uploadAttachment(orgId: string, file: File): Promise<{ id: string; filename: string; created_at: string }> {
        const formData = new FormData();
        formData.append('file', file);
        
        const headers: HeadersInit = {};
        if (this.token) {
            headers['Authorization'] = `Bearer ${this.token}`;
        }
        
        const res = await fetch(`${API_URL}/org/${orgId}/attachments`, {
            method: 'POST',
            headers: headers,
            body: formData
        });
        
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || `Failed to upload attachment`);
        }
        return res.json();
    }

    static async getAttachments(orgId: string): Promise<{ id: string; filename: string; created_at: string }[]> {
        const res = await fetch(`${API_URL}/org/${orgId}/attachments`, {
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error(`Failed to fetch attachments: ${res.status}`);
        return res.json();
    }

    static async deleteAttachment(orgId: string, attachmentId: string): Promise<{ status: string }> {
        const res = await fetch(`${API_URL}/org/${orgId}/attachments/${attachmentId}`, {
            method: 'DELETE',
            headers: this.getHeaders()
        });
        if (!res.ok) {
            const err = await res.json().catch(() => ({}));
            throw new Error(err.detail || `Failed to delete attachment`);
        }
        return res.json();
    }

    static async getAuditLogs(orgId: string): Promise<{ id: string; actor_id: string; actor_username: string; action: string; details: string; created_at: string }[]> {
        const res = await fetch(`${API_URL}/org/${orgId}/audit-logs`, {
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error(`Failed to fetch audit logs: ${res.status}`);
        return res.json();
    }

    static async getAnalytics(orgId: string): Promise<{ total_cost: number; total_input_tokens: number; total_output_tokens: number; projects: { project_id: string; use_case: string; total_cost: number; input_tokens: number; output_tokens: number; created_at: string }[] }> {
        const res = await fetch(`${API_URL}/org/${orgId}/analytics`, {
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error(`Failed to fetch analytics: ${res.status}`);
        return res.json();
    }
    static async getNotifications(limit: number = 50): Promise<{ id: string; user_id: string; title: string; message: string; type: string; action_url?: string; is_read: boolean; created_at: string }[]> {
        const res = await fetch(`${API_URL}/notifications?limit=${limit}`, {
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error(`Failed to fetch notifications: ${res.status}`);
        return res.json();
    }

    static setupNotificationStream(onMessage: (notification: any) => void): EventSource | null {
        if (!this.token) return null;
        
        // Use a hidden input or token param to authenticate SSE
        // In this architecture, it might be easier to use the stream endpoint via URL with token, or rely on cookies.
        // Let's pass token in URL since EventSource doesn't support headers natively in browser
        const es = new EventSource(`${API_URL}/notifications/stream?token=${this.token}`);
        
        es.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.type === 'new_notification') {
                    onMessage(data.data);
                }
            } catch (e) {
                // Ignore parse errors from pings
            }
        };
        
        return es;
    }

    static async markNotificationRead(id: string): Promise<void> {
        const res = await fetch(`${API_URL}/notifications/${id}/read`, {
            method: 'POST',
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error(`Failed to mark notification read: ${res.status}`);
        return res.json();
    }

    static async markAllNotificationsRead(): Promise<void> {
        const res = await fetch(`${API_URL}/notifications/read-all`, {
            method: 'POST',
            headers: this.getHeaders()
        });
        if (!res.ok) throw new Error(`Failed to mark all notifications read: ${res.status}`);
        return res.json();
    }
}

